import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import _Retool from "react-retool";
// react-retool ships CJS; Vite may wrap it as { default: Component }
const Retool = (_Retool as unknown as { default: typeof _Retool }).default ?? _Retool;
import { AlertTriangle, Monitor, ShieldOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@nexus/ui";
import { retoolEmbedUrlQueryOptions } from "@/lib/queries";
import { ApiError } from "@/lib/api-client";

function retoolSrc(base: string): string {
  try {
    const url = new URL(base);
    url.searchParams.set("showlogo", "false");
    return url.toString();
  } catch {
    return base;
  }
}

interface RetoolEmbedProps {
  visible: boolean;
  tenantId: string | null;
}

export function RetoolEmbed({ visible, tenantId: activeTenantId }: RetoolEmbedProps) {
  const [shellInitialized, setShellInitialized] = useState(false);
  const [lastVisibleTenantId, setLastVisibleTenantId] = useState<string | null>(null);

  // Retool keeps one embed session handle per browser tab (`embed-session-token` in
  // sessionStorage on the Retool origin), so a second live panel hijacks the first one's
  // identity and serves the wrong client's data (AD-1748). Deriving from `activeTenantId`
  // rather than storing it keeps two invariants impossible to break: only one panel is ever
  // mounted, and while the workspace is on screen that panel is always the active tenant's.
  // While hidden we hold the last one open so returning to /workspace doesn't reload Retool
  // (AD-805) and switching tenants elsewhere doesn't mint a session we aren't showing (AD-806).
  const renderedTenantId = visible ? activeTenantId : lastVisibleTenantId;

  useEffect(() => {
    if (visible && !shellInitialized) setShellInitialized(true);
  }, [visible, shellInitialized]);

  useEffect(() => {
    if (activeTenantId == null) {
      setLastVisibleTenantId(null);
    } else if (visible) {
      setLastVisibleTenantId(activeTenantId);
    }
  }, [visible, activeTenantId]);

  if (!shellInitialized) return null;

  return (
    <div
      data-testid="retool-embed-container"
      style={{
        display: visible ? "flex" : "none",
        flex: 1,
        flexDirection: "column",
        minHeight: 0,
        colorScheme: "light",
      }}
    >
      <MobileUnavailable />

      <div
        className="hidden md:flex"
        style={{ flex: 1, flexDirection: "column", minHeight: 0, position: "relative" }}
      >
        {renderedTenantId && (
          <TenantRetoolPanel key={renderedTenantId} tenantId={renderedTenantId} />
        )}
      </div>
    </div>
  );
}

interface TenantRetoolPanelProps {
  tenantId: string;
}

function TenantRetoolPanel({ tenantId }: TenantRetoolPanelProps) {
  const { t } = useTranslation();

  const { data, isLoading, isError, error } = useQuery(retoolEmbedUrlQueryOptions(tenantId));

  const isNotProvisioned = isError && error instanceof ApiError && error.code === "RETOOL_NOT_PROVISIONED";

  return (
    <div
      data-testid={`retool-tenant-panel-${tenantId}`}
      style={{ display: "flex", flex: 1, flexDirection: "column", minHeight: 0 }}
    >
      <div style={{ position: "relative", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        {!data && isLoading && <WorkspaceLoadingScreen />}

        {isNotProvisioned && (
          <div
            data-testid="retool-not-provisioned"
            className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-[var(--background)]"
          >
            <ShieldOff className="h-10 w-10 text-[var(--muted-foreground)]" />
            <p className="text-sm font-medium text-[var(--foreground)]">
              {t("workspace.notProvisionedTitle")}
            </p>
            <p className="text-xs text-[var(--muted-foreground)]">
              {t("workspace.notProvisionedBody")}
            </p>
          </div>
        )}

        {isError && !isNotProvisioned && (
          <div
            data-testid="retool-error"
            className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-[var(--background)]"
          >
            <AlertTriangle className="h-10 w-10 text-[var(--muted-foreground)]" />
            <p className="text-sm font-medium text-[var(--foreground)]">
              {t("workspace.loadError")}
            </p>
            <p className="text-xs text-[var(--muted-foreground)]">
              {t("workspace.loadErrorHint")}
            </p>
          </div>
        )}

        {data && (
          <Retool
            url={retoolSrc(data.embedUrl)}
            styling={{ display: "block", width: "100%", flex: 1, border: "none", minHeight: 0 }}
          />
        )}
      </div>
    </div>
  );
}

function WorkspaceLoadingScreen() {
  const { t } = useTranslation();
  // r=18 → circumference = 2π·18 ≈ 113.1
  const circumference = 2 * Math.PI * 18;

  return (
    <div
      data-testid="workspace-loading-screen"
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 20,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--background)",
        gap: 0,
      }}
    >
      {/* Ring mark */}
      <div style={{ position: "relative", width: 48, height: 48 }}>
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden>
          {/* Track */}
          <circle
            cx="24"
            cy="24"
            r="18"
            stroke="oklch(0.91 0.007 264)"
            strokeWidth="1.5"
          />
          {/* Animated arc */}
          <circle
            cx="24"
            cy="24"
            r="18"
            stroke="oklch(0.45 0.1805 264)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference}
            className="ws-ring-trace"
            style={{ transformOrigin: "24px 24px", transform: "rotate(-90deg)" }}
          />
        </svg>

        {/* Center dot */}
        <span
          aria-hidden
          className="ws-center-dot"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: 5,
            height: 5,
            borderRadius: "50%",
            background: "oklch(0.45 0.1805 264)",
          }}
        />
      </div>

      {/* Copy */}
      <div
        className="ws-copy-enter"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 4,
          marginTop: 20,
        }}
      >
        <span
          style={{
            fontSize: 14,
            fontWeight: 500,
            letterSpacing: "-0.015em",
            color: "var(--foreground)",
            lineHeight: 1.4,
          }}
        >
          {t("workspace.loggingIn")}
        </span>
        <span
          style={{
            fontSize: 12,
            letterSpacing: "-0.005em",
            color: "var(--muted-foreground)",
            lineHeight: 1.4,
          }}
        >
          {t("workspace.loggingInHint")}
        </span>
      </div>
    </div>
  );
}

function MobileUnavailable() {
  const { t } = useTranslation();

  return (
    <div
      data-testid="retool-mobile-unavailable"
      className={cn(
        "flex flex-col items-center justify-center gap-6 px-8 md:hidden",
        "h-full min-h-[60svh] bg-[var(--background)]",
      )}
    >
      <div className="relative flex items-center justify-center">
        <div className="absolute h-24 w-24 rounded-full bg-[var(--muted)] opacity-60" />
        <Monitor
          className="relative h-10 w-10 text-[var(--muted-foreground)]"
          strokeWidth={1.5}
          aria-hidden
        />
      </div>

      <div className="flex max-w-[260px] flex-col items-center gap-2 text-center">
        <p className="text-[15px] font-semibold tracking-tight text-[var(--foreground)]">
          {t("workspace.mobileTitle")}
        </p>
        <p className="text-[13px] leading-relaxed text-[var(--muted-foreground)]">
          {t("workspace.mobileBody")}
        </p>
      </div>
    </div>
  );
}
